import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../layouts/DashboardLayout';
import PremiumLockComponent from '../components/PremiumLockComponent';
import { API_BASE_URL } from '../config';
import RecipeModal from '../components/RecipeModal';
import useKitchenPlan from '../hooks/useKitchenPlan';
import PlanningHorizonToggle from '../components/PlanningHorizonToggle';
import PlanDaysPreview from '../components/PlanDaysPreview';
import MealSwapModal from '../components/MealSwapModal';
import HealthMedicalNotice from '../components/HealthMedicalNotice';
import Swal from 'sweetalert2';
import useMenuApproval from '../hooks/useMenuApproval';
import Logo from '../assets/images/arka-saydam-logo.png';
import { copyToClipboard } from '../utils/clipboard';

const getAuthHeaders = (extra = {}) => {
    const token = localStorage.getItem('authToken');
    const user = JSON.parse(localStorage.getItem('user_data') || '{}');
    if (token) return { Authorization: `Bearer ${token}`, ...extra };
    return { ...extra };
};

const DietKitchen = ({ isDarkMode, toggleDarkMode }) => {
    const [loading, setLoading] = useState(true);
    const [hasPremium, setHasPremium] = useState(false);
    const [premiumDenied, setPremiumDenied] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [dietConfig, setDietConfig] = useState(null);
    const [scenario, setScenario] = useState('none'); // 'none', 'dietitian', 'auto'
    const [userProfile, setUserProfile] = useState(null);
    const [isPaused, setIsPaused] = useState(false); // Diet pause state
    const [planModalOpen, setPlanModalOpen] = useState(false); // Plan detail modal
    const [healthPlusEntitled, setHealthPlusEntitled] = useState(false);
    const [healthPlusActive, setHealthPlusActive] = useState(false);
    const [healthPlusBusy, setHealthPlusBusy] = useState(false);

    // Form State for Senaryo B (Auto-calculate)
    const [formData, setFormData] = useState({
        height: '',
        weight: '',
        age: '',
        gender: 'male',
        activityLevel: 'moderate',
        goal: 'maintain'
    });

    const [selectedMember, setSelectedMember] = useState('me'); // 'me', 'partner', 'child_index'

    // [R5] Ortak plan/swap/alışveriş/ufuk mantığı (diet+athlete tek kaynak: useKitchenPlan).
    const {
        planningHorizon, showHorizonModal, setShowHorizonModal,
        weekPlan, setWeekPlan, stockMode, shoppingPayload, shoppingItemCount, creatingShoppingList,
        swapState, setSwapState, selectedRecipe, recipeModalOpen, setRecipeModalOpen, selectedPortion,
        chooseHorizon, onHorizonChange, fetchWeeklyPlan,
        handleCreateShoppingList, openSwap, applySwap, handleRecipeClick
    } = useKitchenPlan('diet');

    // Dietitian Invite System State
    const [inviteLink, setInviteLink] = useState(null);
    const [inviteStatus, setInviteStatus] = useState(null); // 'pending', 'updated', 'expired', 'used'
    const [showInviteUI, setShowInviteUI] = useState(false);
    const [creatingInvite, setCreatingInvite] = useState(false);


    // Member Selection Modal State
    const [showMemberModal, setShowMemberModal] = useState(false);
    const [familyMembers, setFamilyMembers] = useState([]);

    const { approveMenu, isApproving } = useMenuApproval({
        onSuccess: () => {
            fetchWeeklyPlan();
        }
    });

    useEffect(() => {
        if (planModalOpen || recipeModalOpen || showMemberModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [planModalOpen, recipeModalOpen, showMemberModal]);

    useEffect(() => {
        const initialize = async () => {
            console.log('[DietKitchen] Başlatılıyor...');
            setLoading(true);
            try {
                const token = localStorage.getItem('authToken');
                if (!token) {
                    console.error('[DietKitchen] Token bulunamadı!');
                    setLoading(false);
                    return;
                }

                console.log('[DietKitchen] Veriler çekiliyor...');
                const [statusRes, dietRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/profile`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }).catch(err => {
                        console.error('Profile tab hatası:', err);
                        return { data: {}, __error: err };
                    }),
                    axios.get(`${API_BASE_URL}/api/diet/config`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }).catch(err => {
                        console.error('Diet Config hatası:', err);
                        return { data: { dietConfig: null }, __error: err };
                    })
                ]);

                const profileUnauthorized = statusRes?.__error?.response?.status === 401;
                const dietUnauthorized = dietRes?.__error?.response?.status === 401;
                if (profileUnauthorized && dietUnauthorized) {
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('user_data');
                    Swal.fire('Oturum Süresi Doldu', 'Lütfen tekrar giriş yapın.', 'warning');
                    window.location.href = '/login';
                    return;
                }

                console.log('[DietKitchen] API yanıtları alındı');

                // API returns: { userId, user: {...}, children: [...], partnerId: ... }
                const profile = statusRes.data || {};
                const userData = profile.user || {};

                console.log('[DEBUG] FULL PROFILE:', profile);

                const dietGrantedByEntitlement = !!profile?.entitlements?.modules?.diet?.granted;
                const dietFlag = !!userData.hasDietPremium;
                const adminOverride = !!userData.isAdmin;
                const dietDeniedByConfig = dietRes?.__error?.response?.status === 403
                    && ['diet_premium_required', 'subscription_required'].includes(dietRes?.__error?.response?.data?.error);
                setPremiumDenied(!!dietDeniedByConfig);
                const resolvedHasPremium = !dietDeniedByConfig && (
                    adminOverride
                    || dietGrantedByEntitlement
                    || dietFlag
                    || !!token // optimistic fallback: do not lock UI on transient backend/profile issues
                );
                setHasPremium(resolvedHasPremium);
                setIsAdmin(userData.isAdmin || false);
                setUserProfile(profile);
                const resolvedDietConfig = dietRes.data?.dietConfig || null;
                setDietConfig(resolvedDietConfig);
                setIsPaused(!!resolvedDietConfig?.pauseState?.paused);
                setInviteStatus(resolvedDietConfig?.activeInvite?.status || null);
                const healthEntitled = !!profile?.entitlements?.modules?.global?.granted
                    && !profile?.entitlements?.lockedByTime;
                setHealthPlusEntitled(healthEntitled);
                try {
                    const healthStateRes = await axios.get(`${API_BASE_URL}/api/health/activation-state`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setHealthPlusActive(healthEntitled && !!healthStateRes.data?.enabled);
                } catch {
                    setHealthPlusActive(healthEntitled);
                }

                // Parse family members from profile
                const members = [{
                    id: 'me',
                    name: userData.name || 'Ben',
                    type: 'me',
                    photo: userData.avatar
                }];

                // Add partner if exists
                if (profile.partnerId) {
                    members.push({
                        id: 'partner',
                        name: profile.partnerName || 'Eşim',
                        type: 'partner',
                        photo: profile.partnerAvatar || profile.partnerPhoto
                    });
                }

                // Add children if exists (only 2+ years old)
                if (profile.children) {
                    try {
                        const children = typeof profile.children === 'string'
                            ? JSON.parse(profile.children)
                            : profile.children;

                        if (Array.isArray(children)) {
                            children.forEach((child, index) => {
                                // Skip if child is too young (< 2 years old)
                                if (child.birthDate) {
                                    const birthDate = new Date(child.birthDate);
                                    const ageInMonths = (new Date() - birthDate) / (1000 * 60 * 60 * 24 * 30);
                                    if (ageInMonths < 24) {
                                        console.log(`Skipping ${child.name || 'child'} - too young (${Math.floor(ageInMonths)} months)`);
                                        return; // Skip this child
                                    }
                                }

                                // Only add if name is not "Eşim" or similar (partner should be in partnerId)
                                // Also skip babies like Beril (8 months old)
                                const nameLower = child.name?.toLowerCase() || '';
                                if (child.name &&
                                    !nameLower.includes('eşim') &&
                                    !nameLower.includes('samira') &&
                                    !nameLower.includes('beril')) {
                                    members.push({
                                        id: `child_${index}`,
                                        name: child.name || `Çocuk ${index + 1}`,
                                        type: 'child',
                                        photo: child.photo || child.avatar
                                    });
                                }
                            });
                        }
                    } catch (e) {
                        console.warn('Failed to parse children:', e);
                    }
                }

                console.log('[DEBUG] parsed members:', members);
                setFamilyMembers(members);

                console.log('[DEBUG] dietConfig:', dietRes.data?.dietConfig);
                console.log('[DEBUG] hasDietPremium:', userData.hasDietPremium, {
                    entitlementDiet: !!profile?.entitlements?.modules?.diet?.granted,
                    isAdmin: !!userData.isAdmin
                });
                console.log('[DEBUG] familyMembers:', members);

                if (!dietRes.data?.dietConfig) {
                    setScenario('none');
                    // Show member selection modal if no diet config exists
                    console.log('[DEBUG] No dietConfig, checking premium...');
                    if (userData.hasDietPremium) {
                        console.log('[DEBUG] Premium var, modal açılıyor!');
                        setShowMemberModal(true);
                    } else {
                        console.log('[DEBUG] Premium yok, modal açılmıyor');
                    }
                } else {
                    console.log('[DEBUG] DietConfig var, modal açılmıyor');
                    // Set selected member from existing config
                    setSelectedMember(dietRes.data.dietConfig.targetMember || 'me');
                }

                if (userData.hasDietPremium && dietRes.data?.dietConfig) {
                    console.log('[DietKitchen] Tarifler çekiliyor...');
                    await fetchWeeklyPlan();
                }
                if (userData.hasDietPremium) {
                    await fetchInviteStatus();
                }
            } catch (error) {
                console.error('[DietKitchen] Kritik hata:', error);
                setHasPremium(false);
            } finally {
                console.log('[DietKitchen] Yükleme tamamlandı');
                setLoading(false);
            }
        };
        initialize();
    }, []);

    const handleCalculate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const res = await axios.post(`${API_BASE_URL}/api/diet/calculate`,
                { ...formData, mode: 'diet', targetMember: selectedMember }, // Add targetMember
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (res.data.ok) {
                setDietConfig(res.data.dietConfig);
                // [R5-fix] Plan OLUŞTURMA anında günlük/haftalık modalını sor (reset sonrası da gelsin).
                // Planı BURADA çekmiyoruz — kullanıcı modalda günlük/haftalık seçince oluşturulur
                // (chooseHorizon fetch eder). Böylece günlük seçilince haftalık hiç hesaplanmaz.
                setShowHorizonModal(true);
                Swal.fire({
                    title: 'Plan Oluşturuldu!',
                    text: 'Denge planınız başarıyla ayarlandı.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Calculation failed:', error);
            if (error?.response?.status === 409 && error?.response?.data?.error === 'expert_required_for_under_24_months') {
                Swal.fire(
                    'Uzman Gerekli',
                    '24 ay altı çocuk için otomatik plan kapalıdır. Lütfen Diyetisyenle Çalış akışını kullanın.',
                    'warning'
                );
                return;
            }
            Swal.fire('Hata', 'Plan oluşturulurken bir sorun oluştu.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleHealthPlus = async () => {
        if (healthPlusBusy) return;
        if (!healthPlusEntitled) {
            Swal.fire('Bilgi', 'Saglik+ aktivasyonu icin global premium gerekir.', 'info');
            return;
        }
        try {
            setHealthPlusBusy(true);
            const token = localStorage.getItem('authToken');
            const res = await axios.post(
                `${API_BASE_URL}/api/health/activation-state`,
                { enabled: !healthPlusActive },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setHealthPlusActive(!!res.data?.enabled);
        } catch (error) {
            console.error('diet health+ toggle error', error);
            Swal.fire('Hata', 'Saglik+ durumu guncellenemedi.', 'error');
        } finally {
            setHealthPlusBusy(false);
        }
    };



    const handleUpgrade = async () => {
        try {
            const token = localStorage.getItem('authToken');
            // Mock upgrade endpoint - in real app, this would be a checkout sessions
            await axios.post(`${API_BASE_URL}/api/admin/user-upgrade-diet`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            window.location.reload();
        } catch (error) {
            console.error('Upgrade failed:', error);
            Swal.fire('Hata', 'Abonelik işlemi şu an gerçekleştirilemiyor.', 'error');
        }
    };

    // ==================== DIETITIAN INVITE HANDLERS ====================

    const handleCreateInviteLink = async () => {
        setCreatingInvite(true);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/diet/invite-link`,
                { targetMember: selectedMember },
                { headers: getAuthHeaders() }
            );

            if (response.data.ok) {
                const link = response.data.invite.link;
                setInviteLink(link);
                setInviteStatus('pending');
                setShowInviteUI(true);

                const waText = encodeURIComponent(`Şefim selam! BBTV Diyet Mutfağı için planlarımı girmene yardımcı olacak linkim hazır: ${link}`);
                const waUrl = `https://wa.me/?text=${waText}`;

                Swal.fire({
                    icon: 'success',
                    title: 'Davet Linki Oluşturuldu!',
                    html: `
                        <p class="mb-4 text-slate-600">Diyetisyeninizle paylaşabilirsiniz.</p>
                        <a href="${waUrl}" target="_blank" class="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-2xl font-bold hover:bg-[#128C7E] transition-all no-underline">
                            <span class="material-symbols-outlined">share</span>
                            WhatsApp'tan Gönder
                        </a>
                    `,
                    showConfirmButton: true,
                    confirmButtonText: 'Tamam',
                    confirmButtonColor: '#9333ea'
                });
            }
        } catch (error) {
            console.error('Invite link creation error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Hata',
                text: 'Link oluşturulurken bir hata oluştu.'
            });
        } finally {
            setCreatingInvite(false);
        }
    };

    const handleCopyInviteLink = async () => {
        if (inviteLink) {
            const success = await copyToClipboard(inviteLink);
            if (success) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Link kopyalandı!',
                    showConfirmButton: false,
                    timer: 1500
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Hata',
                    text: 'Link kopyalanamadı. Lütfen manuel olarak seçip kopyalayın.'
                });
            }
        }
    };

    const handlePause = async () => {
        const token = localStorage.getItem('authToken');
        if (isPaused) {
            try {
                await axios.post(
                    `${API_BASE_URL}/api/diet/pause`,
                    { paused: false, pausedUntil: null, reason: 'user_resume' },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setIsPaused(false);
            } catch (error) {
                console.error('Diet pause resume error:', error);
                Swal.fire('Hata', 'Plan devam durumu güncellenemedi.', 'error');
                return;
            }
            Swal.fire({
                title: 'Denge Planı Aktif',
                text: 'Mutfak takibi yeniden başladı.',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } else {
            // Pause diet
            const result = await Swal.fire({
                title: 'Denge Planını Duraklat?',
                html: 'Misafir günü veya özel bir durum mu var?<br><small class="text-slate-500">Tarif önerileri normal devam eder, sadece uyumluluk etiketleri gizlenir.</small>',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Evet, Duraklat',
                cancelButtonText: 'İptal',
                confirmButtonColor: '#f59e0b',
                cancelButtonColor: '#64748b'
            });
            if (result.isConfirmed) {
                try {
                    await axios.post(
                        `${API_BASE_URL}/api/diet/pause`,
                        { paused: true, pausedUntil: null, reason: 'user_manual' },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setIsPaused(true);
                } catch (error) {
                    console.error('Diet pause persist error:', error);
                    Swal.fire('Hata', 'Plan duraklatma kaydedilemedi.', 'error');
                    return;
                }
                Swal.fire({
                    title: 'Plan Duraklatıldı',
                    text: 'İyi eğlenceler! Hazır olduğunda tekrar aktif edebilirsin.',
                    icon: 'info',
                    timer: 2500,
                    showConfirmButton: false
                });
            }
        }
    };

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center bg-white dark:bg-[#1a1a1a]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
            </div>
        );
    }

    if (!hasPremium && premiumDenied) {
        return (
            <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
                <div className="max-w-4xl mx-auto py-20 px-6">
                    <PremiumLockComponent
                        title="Diyet Mutfağı"
                        message="Beslenme hedeflerinize mutfağınızdan ulaşın. Kişiselleştirilmiş makro takibi ve denge planını keşfedin. Dolabınızdaki malzemelerle özel diyet planlarınızı uygulayın, diyetisyeninizden size özel planı almak için link paylaşımı yapın."
                        paymentModuleKey="diet"
                        paymentModuleTitle="Diyet Mutfagi"
                    />
                </div>
            </DashboardLayout>
        );
    }

    // DEBUG
    console.log('DietKitchen Debug:', { hasPremium, dietConfig, scenario, loading, showMemberModal });

    // Helper: Check if dietConfig is empty
    const hasValidDietConfig = dietConfig && typeof dietConfig === 'object' && Object.keys(dietConfig).length > 0;

    // Step 1: Scenario Selection (if no config and no scenario chosen)
    if (!hasValidDietConfig && scenario === 'none') {
        return (
            <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
                {/* Member Selection Modal */}
                {showMemberModal && (
                    <div className="fixed inset-0 z-[900] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                        {console.log('[MODAL] Member selection modal RENDERING!')}
                        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl max-w-2xl w-full p-8 animate-in fade-in zoom-in duration-300">
                            <div className="text-center mb-8">
                                <div className="h-20 w-20 bg-purple-500/10 rounded-3xl flex items-center justify-center mx-auto mb-4 border border-purple-100 dark:border-purple-900/50 p-4">
                                    <img src={Logo} alt="Logo" className="w-full h-full object-contain" />
                                </div>
                                <h2 className="text-3xl font-black text-slate-900 dark:text-white mb-2">
                                    Bu Plan Kimin İçin?
                                </h2>
                                <p className="text-slate-600 dark:text-slate-400 text-sm">
                                    Diyet planını oluşturmak istediğiniz aile üyesini seçin
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                {familyMembers.map((member) => (
                                    <button
                                        key={member.id}
                                        onClick={() => {
                                            setSelectedMember(member.id);
                                            setShowMemberModal(false);
                                            // Let user choose scenario manually on the screen behind
                                        }}
                                        className="group p-6 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-700 dark:to-slate-800 hover:from-purple-50 hover:to-indigo-50 dark:hover:from-purple-900/20 dark:hover:to-indigo-900/20 border-2 border-slate-200 dark:border-slate-600 hover:border-purple-500 dark:hover:border-purple-500 rounded-2xl transition-all duration-300 text-left"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="h-14 w-14 bg-white dark:bg-slate-900 rounded-2xl overflow-hidden flex items-center justify-center text-2xl border-2 border-slate-200 dark:border-slate-700 group-hover:border-purple-500 transition-colors shadow-sm">
                                                {member.photo ? (
                                                    <img src={member.photo} alt={member.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <span>{member.type === 'me' ? '👤' : member.type === 'partner' ? '💑' : '👶'}</span>
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-lg font-black text-slate-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
                                                    {member.name}
                                                </h3>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                                    {member.type === 'me' ? 'Kendin için' : member.type === 'partner' ? 'Eşin/Partnerin için' : 'Çocuğun için'}
                                                </p>
                                            </div>
                                            <span className="material-symbols-outlined text-slate-400 group-hover:text-green-500 transition-colors">
                                                arrow_forward
                                            </span>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-2xl p-4">
                                <p className="text-xs text-blue-700 dark:text-blue-300 text-center">
                                    Seçtiğiniz kişi için özel bir diyet planı oluşturulacak. Daha sonra değiştiremezsiniz.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="max-w-5xl mx-auto p-4 md:p-8">
                    <header className="mb-8 text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <span className="material-symbols-outlined text-green-500 text-xl">restaurant</span>
                            <span className="text-[10px] font-black tracking-widest uppercase text-slate-400 dark:text-slate-500">DİYET MUTFAĞI</span>
                        </div>
                        <h1 className="text-4xl font-extrabold text-slate-900 dark:text-white mb-2">
                            Beslenme Planı
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400 text-sm max-w-xl mx-auto">
                            Diyetisyeninizle çalışın veya otomatik hesaplama ile devam edin.
                        </p>
                    </header>

                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Scenario A: Dietitian */}
                        <button
                            onClick={() => setScenario('dietitian')}
                            className="group relative bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-2 border-purple-200 dark:border-purple-800 rounded-[2.5rem] p-8 hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 text-left"
                        >
                            <div className="absolute top-6 right-6 w-16 h-16 bg-purple-500/10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                <span className="material-symbols-outlined text-purple-600 dark:text-purple-400 text-3xl">person</span>
                            </div>
                            <h3 className="text-2xl font-black text-purple-900 dark:text-purple-300 mb-3">
                                Diyetisyenle Çalış
                            </h3>
                            <p className="text-purple-700 dark:text-purple-400 text-sm mb-6 leading-relaxed">
                                Şefim; Sistemin sizin vücut indeksinizi anlayabilmesi için diyetisyeninize özel bir link gönderin. Sizin için hazırladığı makro limitleri otomatik olarak sisteme yüklensin.
                            </p>
                            <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 font-bold text-sm">
                                <span>Link Oluştur</span>
                                <span className="material-symbols-outlined text-lg group-hover:translate-x-1 transition-transform">arrow_forward</span>
                            </div>
                        </button>

                        {/* Scenario B: Auto */}
                        <button
                            onClick={() => setScenario('auto')}
                            className="group relative bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-800 rounded-[2.5rem] p-8 hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 text-left"
                        >
                            <div className="absolute top-6 right-6 w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                                <span className="material-symbols-outlined text-green-600 dark:text-green-400 text-3xl">analytics</span>
                            </div>
                            <h3 className="text-2xl font-black text-green-900 dark:text-green-300 mb-3">
                                Otomatik Plan
                            </h3>
                            <p className="text-green-700 dark:text-green-400 text-sm mb-6 leading-relaxed">
                                Vücut ölçülerinizi ve hedeflerinizi girin. BMR/TDEE hesaplaması ile otomatik makro planı oluşturalım.
                            </p>
                            <div className="flex items-center gap-2 text-green-600 dark:text-green-400 font-bold text-sm">
                                <span>Hemen Başla</span>
                                <span className="material-symbols-outlined text-lg group-hover:translate-x-1 transition-transform">arrow_forward</span>
                            </div>
                        </button>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    // Step 2: Scenario A - Dietitian Link (if dietitian chosen but no config)
    if (!hasValidDietConfig && scenario === 'dietitian') {
        const targetMemberObj = familyMembers.find(m => m.id === selectedMember);
        const targetName = targetMemberObj?.name || 'Şef';
        const targetType = targetMemberObj?.type || 'me';

        return (
            <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
                <div className="max-w-2xl mx-auto px-4 py-12 min-h-[80vh] flex flex-col justify-between">
                    {/* Top Back Button */}
                    <div className="mb-4">
                        <button
                            onClick={() => setScenario('none')}
                            className="flex items-center gap-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-all font-bold text-xs uppercase tracking-tighter"
                        >
                            <span className="material-symbols-outlined text-sm">arrow_back</span>
                            Geri Dön
                        </button>
                    </div>

                    {/* Main Minimal Content */}
                    <div className="flex-1 flex flex-col items-center justify-center text-center space-y-10">
                        <header className="space-y-4">
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-500/5 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-800/50 rounded-full">
                                <span className="material-symbols-outlined text-purple-500 text-sm animate-pulse">lock_person</span>
                                <span className="text-[10px] font-black text-purple-600 dark:text-purple-400 tracking-widest uppercase">PRO GUARD ERİŞİMİ</span>
                            </div>
                            <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-white leading-[1.1]">
                                Profesyonel <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-500 italic font-serif">Diyetisyen</span> Desteği
                            </h1>
                            <p className="text-slate-500 dark:text-slate-400 text-sm md:text-base max-w-sm mx-auto font-medium">
                                Diyetisyeninizin makro planınızı BBTV mutfağına doğrudan işlemesi için bir kapı açın.
                            </p>
                        </header>

                        <div className="w-full max-w-md">
                            {!showInviteUI ? (
                                <div className="space-y-6">
                                    <button
                                        onClick={handleCreateInviteLink}
                                        disabled={creatingInvite}
                                        className="group relative w-full h-16 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-3xl font-black text-lg transition-all hover:scale-[1.02] active:scale-98 disabled:opacity-50 overflow-hidden"
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-indigo-600 opacity-0 group-hover:opacity-10 transition-opacity"></div>
                                        <div className="relative flex items-center justify-center gap-3">
                                            {creatingInvite ? (
                                                <div className="h-5 w-5 border-2 border-current/30 border-t-current rounded-full animate-spin"></div>
                                            ) : (
                                                <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">send_money</span>
                                            )}
                                            Davet Linki Oluştur
                                        </div>
                                    </button>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">72 SAAT BOYUNCA GEÇERLİDİR</p>
                                </div>
                            ) : (
                                <div className="space-y-4 p-8 bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50 rounded-[2.5rem] shadow-xl animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                                            <span className="text-[10px] font-black text-slate-400 tracking-widest">AKTİF LİNK</span>
                                        </div>
                                        <button
                                            onClick={() => setShowInviteUI(false)}
                                            className="text-[10px] font-bold text-purple-600 hover:underline uppercase"
                                        >
                                            YENİLE
                                        </button>
                                    </div>

                                    <div className="flex flex-col gap-3">
                                        <div className="relative group">
                                            <input
                                                readOnly
                                                value={inviteLink}
                                                className="w-full h-14 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-5 pr-12 text-sm font-bold text-slate-600 dark:text-slate-400 transition-colors group-hover:border-purple-300 dark:group-hover:border-purple-800"
                                            />
                                            <button
                                                onClick={async () => {
                                                    const success = await copyToClipboard(inviteLink);
                                                    if (success) {
                                                        Swal.fire({ title: 'Kopyalandı!', toast: true, position: 'top-end', timer: 2000, showConfirmButton: false, icon: 'success' });
                                                    }
                                                }}
                                                className="absolute right-2 top-2 h-10 w-10 flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl text-slate-400 hover:text-purple-600 shadow-sm transition-all active:scale-95"
                                            >
                                                <span className="material-symbols-outlined text-lg">content_copy</span>
                                            </button>
                                        </div>

                                        <a
                                            href={`https://wa.me/?text=${encodeURIComponent(`Şefim selam! BBTV Diyet Mutfağı planlarım hazır: ${inviteLink}`)}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="w-full h-14 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-2xl flex items-center justify-center gap-2 font-bold transition-all shadow-lg shadow-green-500/10 no-underline"
                                        >
                                            <span className="material-symbols-outlined">share</span>
                                            WhatsApp'la Gönder
                                        </a>
                                    </div>


                                    <div className="flex items-center gap-3 justify-center text-[11px] text-slate-400 font-medium pt-2">
                                        <span className="material-symbols-outlined text-xs text-amber-500">sync_alt</span>
                                        Diyetisyeninizin planı işlemesi bekleniyor...
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Bottom Signature Section */}
                    <footer className="mt-12 flex flex-col items-center gap-6 pt-12 border-t border-slate-100 dark:border-slate-800/50">
                        <div className="flex items-center gap-4 bg-white dark:bg-slate-900/50 px-6 py-3 rounded-full border border-slate-100 dark:border-slate-800 shadow-sm">
                            <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-xl">
                                {targetType === 'me' ? '👤' : targetType === 'partner' ? '💑' : '👶'}
                            </div>
                            <div className="text-left">
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">DANIŞAN KİMLİĞİ</p>
                                <p className="text-sm font-black text-slate-900 dark:text-white leading-none">
                                    {targetName} <span className="opacity-30 mx-1">/</span> <span className="text-purple-600">BBTV</span>
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col items-center opacity-90">
                            <img src={Logo} alt="BBTV" className="h-10 w-auto mb-2" />
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.3em]">Bana Bir Tarif Ver</p>
                        </div>
                    </footer>
                </div>
            </DashboardLayout>
        );
    }

    // Step 3: Scenario B - Auto Form (if auto chosen but no config)
    if (!hasValidDietConfig && scenario === 'auto') {
        return (
            <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
                <div className="max-w-[1400px] mx-auto p-4 md:p-8">
                    <header className="mb-8 max-w-2xl">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="material-symbols-outlined text-green-500 text-sm">restaurant</span>
                            <span className="text-xs font-black tracking-widest uppercase text-slate-400 dark:text-slate-500 font-display">DİYET MUTFAĞI</span>
                        </div>
                        <h1 className="text-4xl font-extrabold text-slate-900 dark:text-white mb-3">
                            Yeni Bir <span className="text-green-500 italic">Başlangıç</span> Yapın
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400 text-sm">
                            Vücut ölçülerinizi ve hedeflerinizi girin, BBTV mutfağınızı sizin için optimize etsin.
                            <span className="block mt-1 font-semibold text-green-600 dark:text-green-500">Bu bir tıbbi diyet değil, beslenme denge rehberidir.</span>
                        </p>
                    </header>

                    <div className="bg-white dark:bg-slate-800 rounded-[2.5rem] border border-slate-100 dark:border-slate-700 shadow-xl overflow-hidden max-w-3xl">
                        <form onSubmit={handleCalculate} className="p-8 md:p-12 space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">KİŞİSEL BİLGİLER</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-bold text-slate-600 dark:text-slate-300">BOY (CM)</label>
                                            <input required type="number" placeholder="175" className="w-full h-12 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-4 text-sm font-bold" value={formData.height} onChange={e => setFormData({ ...formData, height: e.target.value })} />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-bold text-slate-600 dark:text-slate-300">KİLO (KG)</label>
                                            <input required type="number" placeholder="70" className="w-full h-12 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-4 text-sm font-bold" value={formData.weight} onChange={e => setFormData({ ...formData, weight: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-bold text-slate-600 dark:text-slate-300">YAŞ</label>
                                            <input required type="number" placeholder="25" className="w-full h-12 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-4 text-sm font-bold" value={formData.age} onChange={e => setFormData({ ...formData, age: e.target.value })} />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-bold text-slate-600 dark:text-slate-300">CİNSİYET</label>
                                            <select className="w-full h-12 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-4 text-sm font-bold" value={formData.gender} onChange={e => setFormData({ ...formData, gender: e.target.value })}>
                                                <option value="male">Erkek</option>
                                                <option value="female">Kadın</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">HEDEFLER</h3>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-slate-600 dark:text-slate-300">AKTİVİTE SEVİYESİ</label>
                                        <select className="w-full h-12 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-4 text-sm font-bold" value={formData.activityLevel} onChange={e => setFormData({ ...formData, activityLevel: e.target.value })}>
                                            <option value="sedentary">Hareketsiz (Ofis başı)</option>
                                            <option value="light">Az Hareketli (Haftada 1-2 spor)</option>
                                            <option value="moderate">Orta Hareketli (Haftada 3-4 spor)</option>
                                            <option value="active">Çok Hareketli (Neredeyse her gün)</option>
                                            <option value="very_active">Profesyonel / Ağır İş</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-slate-600 dark:text-slate-300">ANA HEDEF</label>
                                        <select className="w-full h-12 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-4 text-sm font-bold" value={formData.goal} onChange={e => setFormData({ ...formData, goal: e.target.value })}>
                                            <option value="lose">Kilo Vermek</option>
                                            <option value="maintain">Kiloyu Korumak</option>
                                            <option value="gain">Kilo Almak</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <button type="submit" className="w-full h-16 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-[2rem] font-black text-lg shadow-xl shadow-green-500/20 transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2">
                                <span className="material-symbols-outlined">analytics</span>
                                Planımı Oluştur
                            </button>
                        </form>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    // Step 2: Active Dashboard (Scenario A & B)
    // Step 2: Active Dashboard (Scenario A & B)
    const targets = dietConfig?.effectiveTargets || dietConfig?.configs?.[dietConfig?.activeMode || 'diet']?.targets || {};
    const isDietMode = dietConfig?.mode === 'diet';
    const todayKey = new Date().toISOString().split('T')[0];
    const todayIndex = Math.max(0, weekPlan.findIndex((day) => day.date === todayKey));
    const visiblePlanDays = (() => {
        if (!weekPlan.length) return [];
        if (planningHorizon === 'today') {
            return [weekPlan[todayIndex] || weekPlan[0]].filter(Boolean);
        }
        if (planningHorizon === '3d') {
            return weekPlan.slice(todayIndex, todayIndex + 3);
        }
        return weekPlan.slice(todayIndex, todayIndex + 7);
    })();

    /**
     * Reset meal plan and trigger fresh recommendations
     */
    const handleResetPlan = async () => {
        try {
            const result = await Swal.fire({
                title: 'Planı Sıfırla?',
                text: 'Mevcut öğün seçimleriniz temizlenecek ve başlangıç kurulumuna döneceksiniz.',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Evet, Sıfırla',
                cancelButtonText: 'İptal',
                confirmButtonColor: '#10b981',
                cancelButtonColor: '#6b7280'
            });

            if (result.isConfirmed) {
                const token = localStorage.getItem('authToken');
                await axios.delete(`${API_BASE_URL}/api/diet/config`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                setDietConfig(null);
                setWeekPlan([]);
                setScenario('none');
                setInviteLink(null);
                setInviteStatus(null);
                setShowInviteUI(false);
                setShowMemberModal(true);

                Swal.fire({
                    icon: 'success',
                    title: 'Plan Sıfırlandı!',
                    text: 'Başlangıç kurulum ekranına döndün.',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('[DietKitchen] Reset error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Hata!',
                text: 'Plan sıfırlanırken bir hata oluştu.',
                confirmButtonColor: '#10b981'
            });
        }
    };

    const fetchInviteStatus = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/diet/invite-status`, {
                headers: getAuthHeaders()
            });
            if (res.data?.ok) {
                setInviteStatus(res.data.activeInvite?.status || null);
            }
        } catch (err) {
            console.error('Diet invite status fetch error:', err);
        }
    };

    // Helper to calculate planned stats from mealPlan
    const calculatePlannedStats = () => {
        const stats = { calories: 0, protein: 0, carbs: 0, fat: 0 };
        if (!dietConfig?.mealPlan || typeof dietConfig.mealPlan !== 'object') return stats;

        Object.values(dietConfig.mealPlan).forEach(recipe => {
            if (!recipe || typeof recipe !== 'object') return;
            // Handle different calorie keys and parse safely
            const rawCal = recipe.nutritionalValues?.calories || recipe.calories || recipe.kalori || 0;
            stats.calories += parseInt(rawCal) || 0;

            // Assume recipe has basic macros if nutritionalValues exists
            if (recipe.nutritionalValues) {
                stats.protein += parseInt(recipe.nutritionalValues.protein || 0);
                stats.carbs += parseInt(recipe.nutritionalValues.carbs || 0);
                stats.fat += parseInt(recipe.nutritionalValues.fat || 0);
            }
        });
        return stats;
    };

    const plannedStats = calculatePlannedStats();
    const calorieProgress = Math.min((plannedStats.calories / (targets.dailyCalories || 1)) * 100, 100);

    // [B13-UI] Plandaki ana yemeklerin (öğle+akşam) protein-tipi dağılımı — haftalık iskeleti gösterir.
    const proteinDistribution = (() => {
        const counts = { 'BAKLİYAT': 0, 'TAVUK': 0, 'ET': 0, 'BALIK': 0, 'SEBZE': 0 };
        (Array.isArray(weekPlan) ? weekPlan : []).forEach((day) => {
            ['lunch', 'dinner'].forEach((slot) => {
                const r = (day?.meals?.[slot] || [])[0];
                const k = r?.mealProteinKey;
                if (k && counts[k] !== undefined) counts[k] += 1;
            });
        });
        return counts;
    })();
    const PROTEIN_META = {
        'BAKLİYAT': { icon: '🫘', label: 'Bakliyat' }, 'TAVUK': { icon: '🍗', label: 'Tavuk' },
        'ET': { icon: '🥩', label: 'Et' }, 'BALIK': { icon: '🐟', label: 'Balık' }, 'SEBZE': { icon: '🥗', label: 'Sebze' }
    };
    const hasProteinDist = Object.values(proteinDistribution).some((v) => v > 0);

    return (
        <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 -mt-4 md:-mt-8">
                <header className="mb-3 flex flex-col md:flex-row md:items-center justify-between gap-2">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="material-symbols-outlined text-green-500 text-sm">restaurant</span>
                            <span className="text-[10px] font-black tracking-widest uppercase text-slate-400 dark:text-slate-500 font-display">
                                DİYET MUTFAĞI
                            </span>
                        </div>
                        <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-1">
                            {(!dietConfig?.targetMember || dietConfig?.targetMember === 'me') ? 'Hoş Geldin, ' : ''}
                            <span className="text-green-500">
                                {(!dietConfig?.targetMember || dietConfig?.targetMember === 'me') ? 'Şef' :
                                    dietConfig?.targetMember === 'partner' ? 'Partner Planı' :
                                        dietConfig?.targetMember?.startsWith('child') ? 'Çocuk Planı' : 'Şef'}
                            </span>
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400 text-[10px] font-medium">
                            Bugünkü mutfak kararlarınızı hedefleriniz doğrultusunda optimize ettik.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleToggleHealthPlus}
                            disabled={healthPlusBusy}
                            className={`px-4 py-2 text-xs font-black rounded-xl transition-all flex items-center gap-2 shadow-sm ${
                                healthPlusActive
                                    ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                                    : 'bg-slate-700 hover:bg-slate-800 text-white'
                            }`}
                        >
                            <span className="material-symbols-outlined text-sm">health_and_safety</span>
                            {healthPlusActive ? 'SAGLIK+ AKTIF' : 'SAGLIK+ PASIF'}
                        </button>
                        <button
                            onClick={handleResetPlan}
                            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-black rounded-xl transition-all flex items-center gap-2 shadow-sm"
                        >
                            <span className="material-symbols-outlined text-sm">refresh</span>
                            PLANI SIFIRLA
                        </button>
                    </div>
                </header>

                <PlanningHorizonToggle
                    value={planningHorizon}
                    onChange={onHorizonChange}
                    mode="diet"
                    className="mb-6"
                />

                {/* STICKY PLAN SUMMARY BAND */}
                <div className={`sticky top-0 z-40 bg-white/95 dark:bg-slate-800/95 backdrop-blur-lg rounded-3xl p-4 md:p-5 shadow-lg border-2 mb-6 transition-all ${isPaused ? 'border-amber-300 dark:border-amber-600 bg-amber-50/50 dark:bg-amber-900/20' : dietConfig?.source === 'dietitian' ? 'border-purple-300 dark:border-purple-600' : 'border-green-200 dark:border-green-700'}`}>

                    {/* Professional Plan Badge */}
                    {dietConfig?.source === 'dietitian' && (
                        <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-2xl">
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-purple-600 dark:text-purple-400">medical_services</span>
                                <div className="flex-1">
                                    <p className="text-xs font-black text-purple-900 dark:text-purple-100">
                                        Diyetisyen Planı
                                    </p>
                                    <p className="text-[10px] text-purple-600 dark:text-purple-400">
                                        {dietConfig.notes || 'Profesyonel tarafından belirlenen makro hedefler'}
                                    </p>
                                </div>
                                <span className="text-[10px] text-purple-500 dark:text-purple-400">
                                    {dietConfig.updatedAt && `Son güncelleme: ${new Date(dietConfig.updatedAt).toLocaleDateString('tr-TR')}`}
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                        {/* Left: Macro Summary */}
                        <div className="flex items-center gap-3 md:gap-6 flex-wrap flex-1">
                            <div className="flex items-center gap-3 flex-1 min-w-[200px]">
                                <div className="h-10 w-10 rounded-xl bg-green-500/10 flex items-center justify-center text-green-600 shrink-0">
                                    <span className="material-symbols-outlined text-2xl">local_fire_department</span>
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">GÜNLÜK KALORİ DENGESİ</p>
                                        <p className="text-[10px] font-black text-slate-900 dark:text-white">
                                            {plannedStats.calories} / <span className="text-slate-400">{targets.dailyCalories || 0} kcal</span>
                                        </p>
                                    </div>
                                    <div className="h-2 w-full bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full transition-all duration-1000 ${plannedStats.calories > targets.dailyCalories ? 'bg-red-500' : 'bg-green-500'}`}
                                            style={{ width: `${calorieProgress}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>

                            <div className="h-8 w-[1px] bg-slate-200 dark:bg-slate-700 hidden lg:block"></div>

                            <div className="flex gap-4">
                                <div className="text-center md:text-left">
                                    <p className="text-[9px] font-black text-slate-400 uppercase">Protein</p>
                                    <p className="text-sm font-black text-slate-900 dark:text-white">
                                        {plannedStats.protein}g <span className="text-[10px] text-slate-400 font-medium">/ {targets.protein_min || 0}g+</span>
                                    </p>
                                </div>
                                <div className="text-center md:text-left">
                                    <p className="text-[9px] font-black text-slate-400 uppercase">Karb</p>
                                    <p className="text-sm font-black text-slate-900 dark:text-white">
                                        {plannedStats.carbs}g <span className="text-[10px] text-slate-400 font-medium">/ {targets.carbs_range?.[1] || 0}g</span>
                                    </p>
                                </div>
                                <div className="text-center md:text-left">
                                    <p className="text-[9px] font-black text-slate-400 uppercase">Yağ</p>
                                    <p className="text-sm font-black text-slate-900 dark:text-white">
                                        {plannedStats.fat}g <span className="text-[10px] text-slate-400 font-medium">/ {targets.fat_max || 0}g</span>
                                    </p>
                                </div>
                            </div>

                            {/* [B13-UI] Protein-tipi dağılımı (haftalık iskelet): Bakliyat/Tavuk/Et/Balık */}
                            {hasProteinDist && (
                                <div className="flex flex-wrap items-center gap-1.5 lg:border-l lg:border-slate-200 lg:dark:border-slate-700 lg:pl-4">
                                    {Object.entries(proteinDistribution)
                                        .filter(([, v]) => v > 0)
                                        .map(([key, v]) => (
                                            <span key={key} className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700/50 px-2 py-1 text-[11px] font-bold text-slate-600 dark:text-slate-300">
                                                <span>{PROTEIN_META[key]?.icon}</span>{PROTEIN_META[key]?.label}<span className="text-rose-500">{v}</span>
                                            </span>
                                        ))}
                                </div>
                            )}
                        </div>

                        {/* Right: Actions */}
                        <div className="flex items-center gap-2">
                            {isPaused && (
                                <span className="text-xs font-black text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-3 py-1.5 rounded-full flex items-center gap-1">
                                    <span className="material-symbols-outlined text-sm">pause_circle</span>
                                    Duraklatıldı
                                </span>
                            )}
                            <button
                                onClick={handlePause}
                                className={`px-4 py-2 rounded-2xl font-black text-xs transition-all flex items-center gap-1.5 ${isPaused ? 'bg-green-500 hover:bg-green-600 text-white shadow-lg shadow-green-500/20' : 'bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/30 dark:hover:bg-amber-900/50 text-amber-700 dark:text-amber-400'}`}
                            >
                                <span className="material-symbols-outlined text-sm">{isPaused ? 'play_circle' : 'pause_circle'}</span>
                                {isPaused ? 'Devam Et' : 'Duraklat'}
                            </button>
                            <button
                                onClick={() => setPlanModalOpen(true)}
                                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-400"
                                title="Plan Detayları"
                            >
                                <span className="material-symbols-outlined text-[20px]">info</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Disclaimer */}
                {!isPaused && (
                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 flex items-start gap-1.5">
                            <span className="material-symbols-outlined text-xs text-blue-500 mt-0.5">info</span>
                            <span>Bu bir tıbbi diyet değildir. Beslenme denge rehberidir. Sağlık durumunuz için mutlaka uzman görüşü alın.</span>
                        </p>
                    </div>
                )}
                {/* Sub Navigation - REMOVED AS PER USER REQUEST */}

                <HealthMedicalNotice
                    enabled={healthPlusActive}
                    targetMemberKey={selectedMember || 'me'}
                    className="mb-6"
                />
                {weekPlan.length > 0 && (
                    <div className="mb-8">
                        <div className="mb-4 flex items-center justify-between">
                            <div>
                                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Aktif Gorunum</p>
                                <h2 className="mt-1 text-2xl font-black text-slate-900 dark:text-white">
                                    {planningHorizon === 'today' ? 'Bugün İçin Önerilen Tarifler' : planningHorizon === '3d' ? '3 Gunluk Beslenme Akisi' : '7 Gunluk Beslenme Plani'}
                                </h2>
                            </div>
                            <div className="rounded-full bg-emerald-50 px-3 py-1.5 text-[11px] font-black text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                                Hedef + stok + kisit birlikte degerlendirildi
                            </div>
                        </div>

                        {/* [B14-C3/E] Haftalık eksik malzeme uyarısı — grid ÜSTÜNDE (mobilde ilk görünen). */}
                        {stockMode === 'shop' && (
                            <div className="mb-4 rounded-[2rem] border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 p-5 dark:border-green-800 dark:from-green-900/20 dark:to-emerald-900/20">
                                {shoppingItemCount > 0 ? (
                                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                        <div className="flex items-start gap-3">
                                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-green-500/10 text-green-600 dark:text-green-400">
                                                <span className="material-symbols-outlined">shopping_cart</span>
                                            </div>
                                            <div>
                                                <h3 className="text-base font-black text-green-900 dark:text-green-100">Haftalık plan için eksik malzemeler</h3>
                                                <p className="mt-0.5 text-sm font-medium text-green-700 dark:text-green-300">
                                                    Dolabında olmayan <b>{shoppingItemCount} kalem</b> tek tıkla Market Listelerim'e eklensin.
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleCreateShoppingList()}
                                            disabled={creatingShoppingList}
                                            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-green-600 px-6 py-3 text-sm font-black text-white shadow-lg shadow-green-500/20 transition-all hover:bg-green-700 disabled:opacity-50"
                                        >
                                            <span className="material-symbols-outlined text-lg">{creatingShoppingList ? 'hourglass_top' : 'add_shopping_cart'}</span>
                                            {creatingShoppingList ? 'Oluşturuluyor...' : 'Alışveriş Listesi Oluştur'}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                                            <span className="material-symbols-outlined">check_circle</span>
                                        </div>
                                        <div>
                                            <h3 className="text-base font-black text-emerald-900 dark:text-emerald-100">Stok yeterli</h3>
                                            <p className="mt-0.5 text-sm font-medium text-emerald-700 dark:text-emerald-300">Bu haftalık plan için ekstra alışverişe gerek yok.</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <PlanDaysPreview
                            days={visiblePlanDays}
                            isDarkMode={isDarkMode}
                            onRecipeClick={handleRecipeClick}
                            emptyMessage="Bu gorunum icin henuz uygun plan bulunamadi."
                            onSwap={openSwap}
                            swapTheme="green"
                        />
                    </div>
                )}

                {!weekPlan.length && (
                    <div className="mb-8 rounded-[2rem] border border-dashed border-slate-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-800">
                        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Plan Bekleniyor</p>
                        <h2 className="mt-2 text-2xl font-black text-slate-900 dark:text-white">Beslenme görünümü hazırlanıyor</h2>
                        <p className="mt-2 text-sm font-medium text-slate-500 dark:text-slate-400">
                            Hedefleriniz, stoklarınız ve kısıtlarınız birlikte işlendiğinde burada bugün, 3 gün ve 7 gün görünümü açılacak.
                        </p>
                    </div>
                )}
                <div className="mb-8 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-3xl p-6 border-2 border-purple-200 dark:border-purple-700">
                    <div className="flex items-start gap-4">
                        <div className="h-12 w-12 rounded-2xl bg-purple-500/10 flex items-center justify-center text-purple-600 dark:text-purple-400 shrink-0">
                            <span className="material-symbols-outlined text-3xl">medical_services</span>
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-black text-purple-900 dark:text-purple-100 uppercase tracking-tight mb-2">
                                Diyetisyenle Calis
                            </h3>
                            <p className="text-sm text-purple-700 dark:text-purple-300 mb-4">
                                Profesyonel bir diyetisyen ile calisiyorsaniz, ona ozel bir davet linki olusturun. Diyetisyeniniz sizin icin makro hedefler belirleyebilir.
                            </p>

                            {!showInviteUI ? (
                                <button
                                    onClick={handleCreateInviteLink}
                                    disabled={creatingInvite}
                                    className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-2xl font-black text-sm transition-all flex items-center gap-2 shadow-lg shadow-purple-500/20 disabled:opacity-50"
                                >
                                    <span className="material-symbols-outlined text-lg">link</span>
                                    {creatingInvite ? 'Olusturuluyor...' : 'Davet Linki Olustur'}
                                </button>
                            ) : (
                                <div className="space-y-3">
                                    <div className="p-4 bg-white dark:bg-slate-800 rounded-2xl border border-purple-200 dark:border-purple-700">
                                        <p className="text-[10px] font-black text-purple-600 dark:text-purple-400 uppercase mb-2">Davet Linki</p>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                value={inviteLink || ''}
                                                readOnly
                                                className="flex-1 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-600 dark:text-slate-300 font-mono"
                                            />
                                            <button
                                                onClick={handleCopyInviteLink}
                                                className="px-4 py-2 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded-xl font-black text-xs transition-all flex items-center gap-1"
                                            >
                                                <span className="material-symbols-outlined text-sm">content_copy</span>
                                                Kopyala
                                            </button>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 text-sm">
                                        {inviteStatus === 'pending' && (
                                            <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                                                <span className="material-symbols-outlined text-lg">schedule</span>
                                                <span className="font-bold">Beklemede</span>
                                                <span className="text-xs text-slate-500">- Diyetisyen henuz erismedi</span>
                                            </span>
                                        )}
                                        {inviteStatus === 'updated' && (
                                            <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                                                <span className="material-symbols-outlined text-lg">check_circle</span>
                                                <span className="font-bold">Guncellendi</span>
                                                <span className="text-xs text-slate-500">- Plan diyetisyen tarafindan belirlendi</span>
                                            </span>
                                        )}
                                        {inviteStatus === 'expired' && (
                                            <span className="flex items-center gap-1.5 text-red-600 dark:text-red-400">
                                                <span className="material-symbols-outlined text-lg">error</span>
                                                <span className="font-bold">Suresi Doldu</span>
                                                <span className="text-xs text-slate-500">- 72 saat gecti, yeni link olusturun</span>
                                            </span>
                                        )}
                                        {inviteStatus === 'used' && (
                                            <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                                                <span className="material-symbols-outlined text-lg">lock</span>
                                                <span className="font-bold">Kullanildi</span>
                                                <span className="text-xs text-slate-500">- Bu link artik gecersiz</span>
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="bg-slate-900 dark:bg-black rounded-3xl p-8 text-white relative overflow-hidden">
                    <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
                        <div className="h-20 w-20 bg-white/10 backdrop-blur-xl rounded-2xl flex items-center justify-center flex-shrink-0">
                            <span className="material-symbols-outlined text-4xl text-green-400">info</span>
                        </div>
                        <div>
                            <h4 className="text-lg font-black uppercase mb-2">Nasil Takip Edilir?</h4>
                            <p className="text-sm text-slate-400 leading-relaxed">
                                Bugun, 3 gun ve 7 gun gorunumleri arasinda gecis yaparak hedefinize en uygun tarifleri izleyebilirsiniz.
                                Yemekleri uyguladikca stoklarinizi guncellemeyi unutmayin.
                            </p>
                        </div>
                    </div>
                    <div className="absolute -right-10 -bottom-10 opacity-10">
                        <span className="material-symbols-outlined text-[150px]">restaurant_menu</span>
                    </div>
                </div>

                {/* Recipe Modal */}
                {
                    recipeModalOpen && selectedRecipe && (
                        <RecipeModal
                            onPairingClick={handleRecipeClick}
                            isOpen={recipeModalOpen}
                            onClose={() => setRecipeModalOpen(false)}
                            recipe={selectedRecipe}
                            isDarkMode={isDarkMode}
                            userProfile={userProfile}
                            userPortion={selectedPortion}
                            skillLevel="Acemi"
                        />
                    )
                }

                {/* [B14-D4] Slot değiştirme (swap) modalı */}
                <MealSwapModal
                    open={swapState.open}
                    onClose={() => setSwapState((s) => ({ ...s, open: false }))}
                    loading={swapState.loading}
                    applying={swapState.applying}
                    alternatives={swapState.alternatives}
                    current={swapState.current}
                    mealType={swapState.mealType || 'lunch'}
                    dateLabel={swapState.date ? new Date(swapState.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }) : ''}
                    onSelect={applySwap}
                    theme="green"
                />

                {/* [B14-A] Plan-öncesi mod seçimi modalı (girişte BİR KEZ; sonra toggle ile değişir) */}
                {showHorizonModal && dietConfig && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200">
                        <div className="w-full max-w-lg rounded-3xl bg-white dark:bg-slate-800 p-6 shadow-2xl border-2 border-green-200 dark:border-green-700">
                            <div className="text-center mb-5">
                                <div className="mx-auto mb-3 h-12 w-12 rounded-2xl bg-green-500/10 flex items-center justify-center">
                                    <span className="material-symbols-outlined text-green-500 text-[28px]">restaurant_menu</span>
                                </div>
                                <h3 className="text-xl font-black text-slate-900 dark:text-white">Planını nasıl kuralım?</h3>
                                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">İstediğin zaman değiştirebilirsin.</p>
                            </div>
                            <div className="grid gap-3">
                                <button type="button" onClick={() => chooseHorizon('today')} className="rounded-2xl border-2 border-slate-200 dark:border-slate-700 p-4 text-left hover:border-green-400 hover:bg-green-50/50 dark:hover:bg-green-900/20 transition-all">
                                    <div className="flex items-center gap-3">
                                        <span className="material-symbols-outlined text-amber-500 text-[26px]">light_mode</span>
                                        <div>
                                            <p className="font-black text-slate-900 dark:text-white">Günlük</p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">Bugün dolabındakiyle ne yapabilirsin — alışveriş yok.</p>
                                        </div>
                                    </div>
                                </button>
                                <button type="button" onClick={() => chooseHorizon('7d')} className="rounded-2xl border-2 border-slate-200 dark:border-slate-700 p-4 text-left hover:border-green-400 hover:bg-green-50/50 dark:hover:bg-green-900/20 transition-all">
                                    <div className="flex items-center gap-3">
                                        <span className="material-symbols-outlined text-green-500 text-[26px]">calendar_month</span>
                                        <div>
                                            <p className="font-black text-slate-900 dark:text-white">Haftalık</p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">7 günlük beslenme programı + otomatik alışveriş listesi.</p>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Plan Detail Modal - Moved to root level to fix stacking/overflow issues */}
                {
                    planModalOpen && dietConfig && (
                        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setPlanModalOpen(false)}>
                            <div
                                className="bg-white dark:bg-slate-800 rounded-3xl max-w-md w-full shadow-2xl overflow-hidden relative animate-in zoom-in-95 duration-200"
                                style={{ maxHeight: '85vh' }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div
                                    className="overflow-y-auto max-h-full scrollbar-hide"
                                    style={{
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none',
                                        paddingRight: '0',
                                        WebkitOverflowScrolling: 'touch'
                                    }}
                                >
                                    <style>{`
                                .scrollbar-hide::-webkit-scrollbar {
                                    display: none;
                                }
                            `}</style>
                                    {/* Header */}
                                    <div className="sticky top-0 bg-gradient-to-r from-green-500 to-emerald-600 p-4 z-10 shrink-0 shadow-lg">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h2 className="text-lg font-black text-white leading-none mb-1">Plan Detayları</h2>
                                                <p className="text-green-100 text-[10px] font-medium">Bireysel denge planınızın özeti</p>
                                            </div>
                                            <button
                                                onClick={() => setPlanModalOpen(false)}
                                                className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors backdrop-blur-sm"
                                            >
                                                <span className="material-symbols-outlined text-white text-xl">close</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="p-5 space-y-4 overflow-y-auto custom-scrollbar" style={{ maxHeight: 'calc(85vh - 76px)' }}>
                                        {/* Plan Info */}
                                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-2xl p-4 border border-slate-100 dark:border-slate-700 shadow-sm">
                                            <h3 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase mb-3 tracking-wider flex items-center gap-2">
                                                <span className="material-symbols-outlined text-sm">badge</span>
                                                Plan Kimliği
                                            </h3>
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center py-1 border-b border-slate-100 dark:border-slate-700/50 last:border-0 last:pb-0">
                                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 font-bold">Başlangıç Tarihi</p>
                                                    <p className="text-[11px] font-black text-slate-900 dark:text-white font-mono">
                                                        {dietConfig.createdAt ? new Date(dietConfig.createdAt).toLocaleDateString('tr-TR') : 'Bugün'}
                                                    </p>
                                                </div>
                                                <div className="flex justify-between items-center py-1 border-b border-slate-100 dark:border-slate-700/50 last:border-0 last:pb-0">
                                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 font-bold">Ana Hedef</p>
                                                    <div className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${dietConfig.goal === 'lose' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                                                        {dietConfig.goal === 'lose' ? 'Kilo Verme' : dietConfig.goal === 'gain' ? 'Kilo Alma' : 'Koruma'}
                                                    </div>
                                                </div>
                                                <div className="flex justify-between items-center py-1 border-b border-slate-100 dark:border-slate-700/50 last:border-0 last:pb-0">
                                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 font-bold">Aktivite Düzeyi</p>
                                                    <p className="text-[11px] font-black text-slate-900 dark:text-white capitalize text-right">
                                                        {dietConfig.activityLevel === 'sedentary' ? 'Hareketsiz' :
                                                            dietConfig.activityLevel === 'light' ? 'Hafif Hareketli' :
                                                                dietConfig.activityLevel === 'moderate' ? 'Orta Hareketli' :
                                                                    dietConfig.activityLevel === 'active' ? 'Yüksek' : 'Çok Yüksek'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Macro Targets */}
                                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/10 dark:to-emerald-900/10 rounded-2xl p-4 border border-green-200 dark:border-green-800 shadow-sm relative overflow-hidden">
                                            <div className="absolute top-0 right-0 p-4 opacity-5">
                                                <span className="material-symbols-outlined text-6xl text-green-700">pie_chart</span>
                                            </div>
                                            <h3 className="text-[10px] font-black text-green-700 dark:text-green-400 uppercase mb-3 tracking-wider flex items-center gap-2">
                                                <span className="material-symbols-outlined text-sm">nutrition</span>
                                                Günlük Hedefler
                                            </h3>
                                            <div className="space-y-2 relative z-10">
                                                <div className="flex items-center justify-between p-2 bg-white/60 dark:bg-black/20 rounded-lg">
                                                    <span className="text-[11px] text-slate-700 dark:text-slate-300 font-bold">Günlük Kalori</span>
                                                    <span className="text-sm font-black text-slate-900 dark:text-white">{targets.dailyCalories || 0} <span className="text-[10px] font-normal text-slate-500">kcal</span></span>
                                                </div>
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div className="bg-white/60 dark:bg-black/20 p-2 rounded-lg text-center">
                                                        <div className="text-[9px] text-green-600 font-bold uppercase mb-0.5">Protein</div>
                                                        <div className="text-xs font-black text-slate-800 dark:text-white">{targets.protein_min || 0}g+</div>
                                                    </div>
                                                    <div className="bg-white/60 dark:bg-black/20 p-2 rounded-lg text-center">
                                                        <div className="text-[9px] text-blue-600 font-bold uppercase mb-0.5">Karb</div>
                                                        <div className="text-xs font-black text-slate-800 dark:text-white">{targets.carbs_range?.[0] || 0}-{targets.carbs_range?.[1] || 0}g</div>
                                                    </div>
                                                    <div className="bg-white/60 dark:bg-black/20 p-2 rounded-lg text-center">
                                                        <div className="text-[9px] text-orange-600 font-bold uppercase mb-0.5">Yağ</div>
                                                        <div className="text-xs font-black text-slate-800 dark:text-white">{'<'}{targets.fat_max || 0}g</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Tolerans Info */}
                                        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 border border-amber-200 dark:border-amber-800 flex items-start gap-3">
                                            <span className="material-symbols-outlined text-amber-600 text-xl shrink-0">info</span>
                                            <div>
                                                <h4 className="text-[10px] font-black text-amber-800 dark:text-amber-300 mb-0.5 uppercase">Esnek Tolerans</h4>
                                                <p className="text-[10px] text-amber-700 dark:text-amber-400/80 leading-snug">
                                                    Bu değerler size özel hesaplanmıştır. Günlük alımlarda ±%10 sapma payı normal kabul edilir.
                                                </p>
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="grid grid-cols-2 gap-3 pt-2">
                                            <button
                                                onClick={() => {
                                                    if (window.confirm('Planınızı silmek istediğinize emin misiniz?')) {
                                                        setDietConfig(null);
                                                        setPlanModalOpen(false);
                                                    }
                                                }}
                                                className="bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-xs"
                                            >
                                                <span className="material-symbols-outlined text-sm">delete</span>
                                                Planı Sil
                                            </button>
                                            <button
                                                onClick={() => setPlanModalOpen(false)}
                                                className="bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-xl transition-colors text-xs shadow-lg shadow-slate-900/20"
                                            >
                                                Tamam
                                            </button>
                                        </div>

                                        <p className="text-[9px] text-center text-slate-400 dark:text-slate-600 mt-2">
                                            Bu plan tıbbi tavsiye niteliği taşımaz. Sadece denge rehberidir.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }
            </div>
        </DashboardLayout>
    );
};

export default DietKitchen;

